/**
 * KpiV2 — Page « KPI & Reporting » (V2 Esprit Padel OS).
 *
 * Volets couverts : filtres (plateforme/période/campagne/format/club), tableau de bord,
 * tableau des publications, classement annuel par progression, suivi campagnes/opérations
 * (saisie manuelle), bilans exportables + historique, et transparence source/fraîcheur.
 *
 * Données : ep:reporting (résumé mensuel/club) + ep:reporting-details:<mois>:<club>.platforms.
 * Écritures : ep:kpi-manual (saisie manuelle) et ep:kpi-reports (historique) via useKpiWrite.
 */
import React, { useState, useMemo, useEffect } from "react";
import {
  useReporting, useClubs, useObjectives, useCampagnes, useCalendarEvents,
  useSyncStatus, useKpiManual, useKpiReports, useReportingDetails,
} from "../hooks/useV1Data";
import { upsertManualEntry, deleteManualEntry, saveReport, deleteReport } from "../hooks/useKpiWrite";
import {
  computeClubGrowth, computeGrowthRanking, metricKind,
} from "../lib/ranking";
import {
  PLATFORMS, PLATFORM_BY_ID, PLATFORM_INDICATORS, RANKING_INDICATORS, INDICATORS,
  NATIONAL_CLUB_ID, CHANNEL_TO_PLATFORM, detailsToPlatforms, platformPublications,
  nationalAggregate, resolveTarget, formatMonth, monthKey, prevMonthKey, monthsOfYear,
  availableMonths, fmtValue, deltaPct, ENGAGEMENT_DEFINITION, MONTH_NAMES,
  REPORTING_ENTITIES, ENTITY_FAMILIES,
} from "../lib/kpiModel";

/* ══════════════════════════════════════ helpers ══════════════════════════ */

function resolveClub(clubId, clubs) {
  const all = [...(clubs || []), ...REPORTING_ENTITIES];
  const c = all.find((x) => String(x.id) === String(clubId));
  if (c) return { id: c.id, name: c.name, color: c.color || "#9896a0" };
  return { id: clubId, name: "Club", color: "#9896a0" };
}

/** Résumé d'une plateforme depuis une ligne ep:reporting (rétro-compat Instagram au top-level). */
function rowPlatformSummary(row, platform) {
  if (!row) return null;
  if (row.platforms && row.platforms[platform]) {
    const p = row.platforms[platform];
    if (p && p.available === false) return null;
    return p;
  }
  if (platform === "instagram") return row; // ancien format
  return null;
}

function indicatorValue(summary, indicator) {
  if (!summary) return null;
  const v = summary[indicator];
  if (v == null || (typeof v === "number" && isNaN(v))) return null;
  return Number(v);
}

/** Combine plusieurs résumés (multi-mois pour un club, ou multi-clubs).
 *  Générique : additionne tous les champs numériques présents, puis recalcule
 *  les champs dérivés selon la famille (réseaux sociaux / GMB / Web GA4). */
function combineSummaries(list, followersMode = "last") {
  const rows = (list || []).filter(Boolean);
  if (!rows.length) return null;
  const isWeb = rows.some((r) => r.source === "ga4" || r.utilisateurs !== undefined || r.sessions !== undefined);

  // Somme de tous les champs numériques présents.
  const out = {};
  const numKeys = new Set();
  rows.forEach((r) => Object.keys(r).forEach((k) => { if (typeof r[k] === "number") numKeys.add(k); }));
  numKeys.forEach((k) => { out[k] = rows.reduce((t, r) => t + Number(r[k] || 0), 0); });

  if (isWeb) {
    const sessions = out.sessions || 0;
    out.tauxEngagement = sessions > 0 ? Number((((out.sessionsEngagees || 0) / sessions) * 100).toFixed(2)) : 0;
    out.tauxRebond = sessions > 0 ? Number((100 - out.tauxEngagement).toFixed(2)) : 0;
    out.pagesParSession = sessions > 0 ? Number(((out.pagesVues || 0) / sessions).toFixed(2)) : 0;
    const totDur = rows.reduce((t, r) => t + Number(r.dureeMoySession || 0) * Number(r.sessions || 0), 0);
    out.dureeMoySession = sessions > 0 ? Math.round(totDur / sessions) : 0;
    out.source = "ga4";
    return out;
  }

  // Réseaux sociaux / GMB.
  out.abonnes = followersMode === "sum"
    ? rows.reduce((t, r) => t + Number(r.abonnes || 0), 0)
    : Number(rows[rows.length - 1].abonnes || 0);
  out.porteeMoy = out.publications > 0 ? Math.round((out.porteeTotale || 0) / out.publications) : 0;
  out.engagement = out.porteeTotale > 0 ? Number((((out.interactions || 0) / out.porteeTotale) * 100).toFixed(2)) : 0;
  const firstFollowers = out.abonnes - (out.evolutionAbonnes || 0);
  out.txEvol = firstFollowers > 0 ? Number((((out.evolutionAbonnes || 0) / firstFollowers) * 100).toFixed(2)) : 0;
  const ratings = rows.map((r) => Number(r.avgRating || 0)).filter((n) => n > 0);
  if (ratings.length) out.avgRating = Number((ratings.reduce((t, n) => t + n, 0) / ratings.length).toFixed(2));
  return out;
}

/** Mois couverts par la période choisie (+ fenêtre précédente comparable). */
function periodMonths(periodType, anchorMonth) {
  const [y, m] = anchorMonth.split("-").map(Number);
  const back = (n) => {
    const d = new Date(y, m - 1 - n, 1);
    return monthKey(d);
  };
  let months;
  if (periodType === "annee") {
    months = monthsOfYear(y).filter((mk) => mk <= anchorMonth);
  } else if (periodType === "trimestre") {
    months = [back(2), back(1), back(0)];
  } else {
    months = [anchorMonth]; // mois / semaine / perso → granularité mensuelle des données
  }
  const len = months.length;
  // Fenêtre précédente comparable : les `len` mois précédant immédiatement la fenêtre.
  const prev = [];
  for (let i = len; i >= 1; i -= 1) prev.push(back((len - 1) + i));
  return { months, prevMonths: prev };
}

const PERIOD_OPTIONS = [
  { id: "semaine", label: "Semaine", approx: true },
  { id: "mois", label: "Mois" },
  { id: "trimestre", label: "Trimestre" },
  { id: "annee", label: "Année" },
  { id: "perso", label: "Dates perso.", approx: true },
];
const FORMAT_OPTIONS = [
  { id: "", label: "Tous formats" },
  { id: "post", label: "Post" },
  { id: "reel", label: "Reel" },
  { id: "story", label: "Story" },
];

/* Abréviations de mois pour les axes de graphe. */
const MONTH_ABBR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const monthAbbr = (mk) => MONTH_ABBR[parseInt(String(mk).split("-")[1], 10) - 1] || mk;

/* Logos officiels des réseaux (SVG inline, pour identifier la plateforme). */
function PlatformIcon({ id, size = 16 }) {
  const s = { width: size, height: size, display: "inline-block", verticalAlign: "-3px", flexShrink: 0 };
  if (id === "instagram") {
    return (
      <svg style={s} viewBox="0 0 24 24" aria-hidden="true">
        <defs>
          <radialGradient id="epig" cx="30%" cy="107%" r="150%">
            <stop offset="0" stopColor="#fdf497" /><stop offset=".05" stopColor="#fdf497" />
            <stop offset=".45" stopColor="#fd5949" /><stop offset=".6" stopColor="#d6249f" />
            <stop offset=".9" stopColor="#285AEB" />
          </radialGradient>
        </defs>
        <rect x="1.5" y="1.5" width="21" height="21" rx="6" fill="url(#epig)" />
        <circle cx="12" cy="12" r="4.6" fill="none" stroke="#fff" strokeWidth="2" />
        <circle cx="17.6" cy="6.4" r="1.3" fill="#fff" />
      </svg>
    );
  }
  if (id === "facebook") {
    return (
      <svg style={s} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="11" fill="#1877F2" />
        <path d="M15.3 8.2h-1.6c-.5 0-.9.4-.9 1v1.8h2.4l-.4 2.5h-2V21h-2.6v-6.5H8.1v-2.5h2.1V9.6c0-2 1.2-3.1 3-3.1.9 0 1.8.1 1.8.1l.3 2.1z" fill="#fff" />
      </svg>
    );
  }
  if (id === "gmb") {
    return (
      <svg style={s} viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#4285F4" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.9 35.7 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z" />
      </svg>
    );
  }
  // web
  return (
    <svg style={s} viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="1.7" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M3 12h18" />
      <path d="M12 3c2.6 2.6 2.6 15.4 0 18M12 3c-2.6 2.6-2.6 15.4 0 18" />
    </svg>
  );
}

/* ══════════════════════════════════════ page ═════════════════════════════ */

export default function KpiV2({ appId, currentUser }) {
  const isAdmin = !!(currentUser && currentUser.admin);
  const { reporting } = useReporting();
  const { clubs } = useClubs();
  const { objectives } = useObjectives();
  const { campagnes } = useCampagnes();
  const { events } = useCalendarEvents();
  const { syncStatus } = useSyncStatus();
  const { kpiManual } = useKpiManual();
  const { kpiReports } = useKpiReports();

  const reportingMap = reporting && typeof reporting === "object" && !Array.isArray(reporting) ? reporting : {};

  // Clubs visibles selon le rôle. France (synthèse) réservé à l'admin.
  const visibleClubs = useMemo(() => {
    if (isAdmin) return clubs;
    const mine = (currentUser?.clubs || []).map(String);
    return (clubs || []).filter((c) => mine.includes(String(c.id)));
  }, [clubs, currentUser, isAdmin]);
  const rankingClubs = useMemo(
    () => (clubs || []).filter((c) => String(c.id) !== String(NATIONAL_CLUB_ID)),
    [clubs]
  );

  const months = availableMonths(reportingMap);
  const defaultMonth = months[months.length - 1] || monthKey(new Date(new Date().setMonth(new Date().getMonth() - 1)));

  // ── État des filtres ──
  const [view, setView] = useState("dashboard");
  const [platform, setPlatform] = useState("instagram");
  const [periodType, setPeriodType] = useState("mois");
  const [anchorMonth, setAnchorMonth] = useState(defaultMonth);
  const [selectedClubIds, setSelectedClubIds] = useState(
    () => (isAdmin ? [] : (currentUser?.clubs || []).map(String)) // [] = tous (admin)
  );
  const [campaignId, setCampaignId] = useState("");
  const [format, setFormat] = useState("");

  const monthOptions = useMemo(() => {
    const set = new Set(months);
    const start = new Date(2025, 11, 1);
    const now = new Date();
    const d = new Date(start);
    while (d <= now) { set.add(monthKey(d)); d.setMonth(d.getMonth() + 1); }
    return [...set].sort().reverse();
  }, [months]);

  // Entités hors clubs (restaurants, national) : visibles pour l'admin.
  const entities = isAdmin ? REPORTING_ENTITIES : [];
  // Pool sélectionnable = clubs padel (hors France) + entités.
  const selectablePool = useMemo(
    () => [...visibleClubs.filter((c) => String(c.id) !== String(NATIONAL_CLUB_ID)), ...entities],
    [visibleClubs, entities]
  );
  // Clubs/entités effectivement ciblés. Par défaut (aucune sélection) = clubs padel seuls
  // (on n'agrège pas restaurants + clubs ensemble sans choix explicite).
  const targetClubs = useMemo(() => {
    const padel = visibleClubs.filter((c) => String(c.id) !== String(NATIONAL_CLUB_ID));
    if (!selectedClubIds.length) return padel;
    return selectablePool.filter((c) => selectedClubIds.includes(String(c.id)));
  }, [visibleClubs, selectablePool, selectedClubIds]);

  const platformAvailableIndicators = PLATFORM_INDICATORS[platform] || [];

  const [resyncBusy, setResyncBusy] = useState(false);
  const [resyncMsg, setResyncMsg] = useState("");
  const doResync = async () => {
    setResyncBusy(true);
    setResyncMsg("");
    try {
      const r = await fetch("/api/admin/resync-metricool", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ count: 6 }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.started) {
        setResyncMsg(`Resynchronisation lancée (${(d.months || []).length || 6} mois). Les données se mettront à jour d'ici 1 à 2 minutes.`);
      } else if (d.error === "resync déjà en cours") {
        setResyncMsg("Une resynchronisation est déjà en cours.");
      } else {
        setResyncMsg(`Échec : ${d.error || r.status}`);
      }
    } catch {
      setResyncMsg("Erreur réseau.");
    } finally {
      setResyncBusy(false);
    }
  };

  return (
    <div className="v2-page v2-page-full">
      <div className="v2-page-header">
        <div className="v2-page-header__left">
          <div className="v2-page-header__sub">
            Pilotage de la performance social media {isAdmin ? "— tous les clubs" : "— votre club"}
          </div>
        </div>
        {isAdmin && (
          <div className="v2-page-header__actions">
            {resyncMsg && <span className="v2-kpi-resync-msg">{resyncMsg}</span>}
            <button
              className="v2-btn v2-btn--sm"
              onClick={doResync}
              disabled={resyncBusy || (syncStatus && syncStatus.running)}
              title="Recharge les 6 derniers mois depuis Metricool (en arrière-plan)"
            >
              ↻ Resynchroniser
            </button>
          </div>
        )}
      </div>

      <div className="v2-kpi-page">
        <FiltersBar
          isAdmin={isAdmin}
          view={view}
          platform={platform} setPlatform={setPlatform}
          periodType={periodType} setPeriodType={setPeriodType}
          anchorMonth={anchorMonth} setAnchorMonth={setAnchorMonth}
          monthOptions={monthOptions}
          clubs={visibleClubs.filter((c) => String(c.id) !== String(NATIONAL_CLUB_ID))}
          entities={entities}
          selectedClubIds={selectedClubIds} setSelectedClubIds={setSelectedClubIds}
          campagnes={campagnes} campaignId={campaignId} setCampaignId={setCampaignId}
          format={format} setFormat={setFormat}
        />

        <DataFreshnessBanner syncStatus={syncStatus} platform={platform} anchorMonth={anchorMonth} />

        {platform === "web" && isAdmin && <Ga4ConnectPanel />}

        <nav className="v2-kpi-tabs">
          {[
            ["dashboard", "Tableau de bord"],
            ["publications", "Publications"],
            ["classement", "Classement"],
            ["campagnes", "Campagnes & opérations"],
            ["rapports", "Bilans"],
          ].map(([id, label]) => (
            <button
              key={id}
              className={`v2-kpi-tab ${view === id ? "is-active" : ""}`}
              onClick={() => setView(id)}
            >
              {label}
            </button>
          ))}
        </nav>

        {view === "dashboard" && (
          <DashboardView
            reportingMap={reportingMap} clubs={clubs} targetClubs={targetClubs}
            platform={platform} periodType={periodType} anchorMonth={anchorMonth}
            objectives={objectives} indicators={platformAvailableIndicators} isAdmin={isAdmin}
          />
        )}
        {view === "publications" && (
          <PublicationsView
            clubs={clubs} targetClubs={targetClubs} platform={platform}
            anchorMonth={anchorMonth} format={format} campaignId={campaignId} campagnes={campagnes}
          />
        )}
        {view === "classement" && (
          <RankingView
            reportingMap={reportingMap} clubs={rankingClubs} platform={platform}
            anchorMonth={anchorMonth} isAdmin={isAdmin} currentUser={currentUser}
          />
        )}
        {view === "campagnes" && (
          <CampaignsOpsView
            campagnes={campagnes} events={events} clubs={clubs} targetClubs={targetClubs}
            kpiManual={kpiManual} appId={appId} isAdmin={isAdmin}
          />
        )}
        {view === "rapports" && (
          <ReportsView
            reportingMap={reportingMap} clubs={clubs} targetClubs={targetClubs}
            anchorMonth={anchorMonth} objectives={objectives}
            kpiReports={kpiReports} kpiManual={kpiManual} campagnes={campagnes} events={events}
            appId={appId} isAdmin={isAdmin}
          />
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════ filters ═════════════════════════ */

function ClubSelect({ isAdmin, clubs, entities, selected, setSelected }) {
  const [open, setOpen] = useState(false);
  const padelIds = (clubs || []).map((c) => String(c.id));
  const restaurants = (entities || []).filter((e) => e.family === "restaurant");
  const national = (entities || []).filter((e) => e.family === "national");
  const nameById = (id) => {
    const c = (clubs || []).find((x) => String(x.id) === String(id)) || (entities || []).find((x) => String(x.id) === String(id));
    return c ? c.name : id;
  };
  // Invariant : sélection vide = tous les clubs padel (synthèse France).
  const norm = (arr) => {
    const padelSel = arr.filter((x) => padelIds.includes(x));
    const entSel = arr.filter((x) => !padelIds.includes(x));
    if (!entSel.length && padelSel.length === padelIds.length) return [];
    return arr;
  };
  const isPadelAll = !selected.length;
  const padelChecked = (id) => isPadelAll || selected.includes(String(id));
  const entChecked = (id) => selected.includes(String(id));
  const togglePadel = (id) => {
    const s = String(id);
    let next = isPadelAll ? padelIds.filter((x) => x !== s)
      : selected.includes(s) ? selected.filter((x) => x !== s)
      : [...selected, s];
    setSelected(norm(next));
  };
  const toggleEntity = (id) => {
    const s = String(id);
    const base = selected.includes(s) ? selected.filter((x) => x !== s)
      : [...(selected.length ? selected : padelIds), s];
    setSelected(norm(base));
  };

  const count = selected.length;
  const label = !count ? "Tous les clubs padel" : count === 1 ? nameById(selected[0]) : `${count} sélectionnés`;
  const Item = ({ e, checked, onToggle }) => (
    <label className="v2-kpi-dd__item">
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <span className="v2-kpi-chip__dot" style={{ background: e.color }} />
      <span>{e.name}</span>
    </label>
  );

  return (
    <div className="v2-kpi-dd">
      <button className="v2-kpi-select v2-kpi-dd__btn" onClick={() => isAdmin && setOpen((o) => !o)} disabled={!isAdmin}>
        <span className="v2-kpi-dd__value">{label}</span>
        <span className="v2-kpi-dd__caret">▾</span>
      </button>
      {open && (
        <>
          <div className="v2-kpi-dd__backdrop" onClick={() => setOpen(false)} />
          <div className="v2-kpi-dd__panel">
            <button className={`v2-kpi-dd__reset ${isPadelAll ? "is-active" : ""}`} onClick={() => setSelected([])}>
              Tous les clubs padel <small>(synthèse France)</small>
            </button>
            <div className="v2-kpi-dd__group">Clubs padel</div>
            {(clubs || []).map((c) => <Item key={c.id} e={c} checked={padelChecked(c.id)} onToggle={() => togglePadel(c.id)} />)}
            {restaurants.length > 0 && <div className="v2-kpi-dd__group">Restaurants</div>}
            {restaurants.map((e) => <Item key={e.id} e={e} checked={entChecked(e.id)} onToggle={() => toggleEntity(e.id)} />)}
            {national.length > 0 && <div className="v2-kpi-dd__group">National</div>}
            {national.map((e) => <Item key={e.id} e={e} checked={entChecked(e.id)} onToggle={() => toggleEntity(e.id)} />)}
          </div>
        </>
      )}
    </div>
  );
}

function FiltersBar({
  isAdmin, platform, setPlatform, periodType, setPeriodType, anchorMonth, setAnchorMonth,
  monthOptions, clubs, entities, selectedClubIds, setSelectedClubIds, campagnes, campaignId, setCampaignId,
  format, setFormat,
}) {
  return (
    <div className="v2-kpi-filters">
      <div className="v2-kpi-filter">
        <span className="v2-kpi-filter__label">Plateforme</span>
        <div className="v2-kpi-seg">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              className={`v2-kpi-seg__btn ${platform === p.id ? "is-active" : ""}`}
              onClick={() => setPlatform(p.id)}
              title={p.label}
            >
              <PlatformIcon id={p.id} /> {p.short}
            </button>
          ))}
        </div>
      </div>

      <div className="v2-kpi-filter">
        <span className="v2-kpi-filter__label">Période</span>
        <select className="v2-kpi-select" value={periodType} onChange={(e) => setPeriodType(e.target.value)}>
          {PERIOD_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>{o.label}{o.approx ? " (≈ mois)" : ""}</option>
          ))}
        </select>
        <select className="v2-kpi-select" value={anchorMonth} onChange={(e) => setAnchorMonth(e.target.value)}>
          {monthOptions.map((m) => <option key={m} value={m}>{formatMonth(m)}</option>)}
        </select>
      </div>

      <div className="v2-kpi-filter">
        <span className="v2-kpi-filter__label">Format</span>
        <select className="v2-kpi-select" value={format} onChange={(e) => setFormat(e.target.value)}>
          {FORMAT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </div>

      <div className="v2-kpi-filter">
        <span className="v2-kpi-filter__label">Campagne</span>
        <select className="v2-kpi-select" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
          <option value="">Toutes</option>
          {(campagnes || []).map((c) => <option key={c.id} value={c.id}>{c.name || "Campagne"}</option>)}
        </select>
      </div>

      <div className="v2-kpi-filter">
        <span className="v2-kpi-filter__label">{isAdmin ? "Clubs & entités" : "Club"}</span>
        <ClubSelect
          isAdmin={isAdmin}
          clubs={clubs}
          entities={entities}
          selected={selectedClubIds}
          setSelected={setSelectedClubIds}
        />
      </div>
    </div>
  );
}

/* Panneau admin de connexion du site web à Google Analytics 4 (OAuth). */
function Ga4ConnectPanel() {
  const [status, setStatus] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await fetch("/api/ga4/status", { credentials: "include" });
      if (r.ok) setStatus(await r.json());
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    try {
      const p = new URLSearchParams(window.location.search).get("ga4");
      if (p === "connected") setMsg("✓ Google Analytics 4 connecté.");
      else if (p === "notoken") setMsg("Google n'a pas renvoyé de jeton — reclique « Autoriser Google ».");
      else if (p === "error") setMsg("Échec de la connexion. Vérifie l'URL de redirection et réessaie.");
    } catch { /* ignore */ }
  }, []);

  const saveConfig = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await fetch("/api/ga4/config", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: clientId.trim(), clientSecret: clientSecret.trim() }),
      });
      if (r.ok) { setMsg("Identifiants enregistrés — clique « Autoriser Google »."); await load(); }
      else { const d = await r.json().catch(() => ({})); setMsg("Erreur : " + (d.error || r.status)); }
    } catch { setMsg("Erreur réseau."); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    await fetch("/api/ga4/disconnect", { method: "POST", credentials: "include" });
    setMsg("GA4 déconnecté."); load();
  };

  if (!status) return null;
  if (status.connected) {
    return (
      <div className="v2-kpi-ga4 v2-kpi-ga4--ok">
        <span>✓ Site web connecté à Google Analytics 4.</span>
        <button className="v2-btn v2-btn--sm v2-btn--ghost" onClick={disconnect}>Déconnecter</button>
        {msg && <span className="v2-kpi-ga4__msg">{msg}</span>}
      </div>
    );
  }
  return (
    <div className="v2-kpi-ga4">
      <div className="v2-kpi-ga4__title">Connecter le site web (Google Analytics 4)</div>
      <ol className="v2-kpi-ga4__steps">
        <li>Google Cloud (projet <i>EspritPadel Reporting</i>) → <b>Clients</b> → créer un client OAuth <b>« Application Web »</b> avec cette URL de redirection autorisée :
          <br /><code className="v2-kpi-ga4__uri">{status.redirectUri}</code></li>
        <li>Colle son <b>ID client</b> et son <b>code secret</b> ci-dessous, puis <b>Enregistrer</b>.</li>
        <li>Clique <b>Autoriser Google</b> et valide le consentement.</li>
      </ol>
      <div className="v2-kpi-ga4__form">
        <input className="v2-form-input" placeholder="ID client (…apps.googleusercontent.com)" value={clientId} onChange={(e) => setClientId(e.target.value)} />
        <input className="v2-form-input" placeholder="Code secret (GOCSPX-…)" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} />
        <button className="v2-btn v2-btn--sm" onClick={saveConfig} disabled={busy || !clientId.trim() || !clientSecret.trim()}>Enregistrer</button>
        <a
          className={`v2-btn v2-btn--sm v2-btn--primary ${status.hasConfig ? "" : "v2-kpi-ga4__disabled"}`}
          href={status.hasConfig ? "/api/ga4/oauth/start" : undefined}
          onClick={(e) => { if (!status.hasConfig) { e.preventDefault(); setMsg("Enregistre d'abord l'ID client + le secret."); } }}
        >Autoriser Google</a>
      </div>
      {msg && <div className="v2-kpi-ga4__msg">{msg}</div>}
    </div>
  );
}

function DataFreshnessBanner({ syncStatus, platform, anchorMonth }) {
  if (!syncStatus) {
    return (
      <div className="v2-kpi-fresh v2-kpi-fresh--muted">
        <span>Source : Metricool</span>
        <span>Aucune synchronisation enregistrée pour le moment.</span>
      </div>
    );
  }
  const date = syncStatus.syncedAt ? new Date(syncStatus.syncedAt).toLocaleString("fr-FR") : "—";
  const netOk = Array.isArray(syncStatus.networks) && syncStatus.networks.includes(platform === "web" ? "web" : platform);
  const webNote = platform === "web";
  return (
    <div className={`v2-kpi-fresh ${syncStatus.ok === false ? "v2-kpi-fresh--error" : ""}`}>
      <span><strong>Source</strong> : Metricool</span>
      <span><strong>Période mesurée</strong> : {formatMonth(anchorMonth)}</span>
      <span><strong>Dernière synchro</strong> : {date}</span>
      {syncStatus.running && (
        <span className="v2-kpi-fresh__sync">
          ⏳ Resynchronisation en cours{syncStatus.progress ? ` (${syncStatus.progress.current || "…"} · ${syncStatus.progress.done}/${syncStatus.progress.total})` : ""}…
        </span>
      )}
      {syncStatus.ok === false && !syncStatus.running && <span className="v2-kpi-fresh__err">⚠ Erreur de récupération : {syncStatus.error || "inconnue"}</span>}
      {webNote && <span className="v2-kpi-fresh__warn">Source du site web : Google Analytics 4.</span>}
      {!webNote && !netOk && <span className="v2-kpi-fresh__warn">Réseau non encore inclus dans la synchro.</span>}
    </div>
  );
}

/* ══════════════════════════════════════ dashboard ═══════════════════════ */

function DashboardView({ reportingMap, clubs, targetClubs, platform, periodType, anchorMonth, objectives, indicators, isAdmin }) {
  const { months, prevMonths } = periodMonths(periodType, anchorMonth);

  // Résumé par club sur la période (followers = dernier mois).
  const clubSummary = (clubId, monthsArr) => {
    const list = monthsArr
      .map((mk) => (reportingMap[mk] || []).find((r) => String(r.clubId) === String(clubId)))
      .map((r) => rowPlatformSummary(r, platform))
      .filter(Boolean);
    return combineSummaries(list, "last");
  };

  const perClub = targetClubs.map((c) => ({
    club: c,
    cur: clubSummary(c.id, months),
    prev: clubSummary(c.id, prevMonths),
  }));
  const withData = perClub.filter((x) => x.cur);

  const single = targetClubs.length === 1 ? withData[0] : null;
  const scopeCur = single ? single.cur : combineSummaries(withData.map((x) => x.cur), "sum");
  const scopePrev = single ? single.prev : combineSummaries(withData.map((x) => x.prev), "sum");
  const scopeLabel = single ? single.club.name : targetClubs.length === clubs.filter((c) => String(c.id) !== String(NATIONAL_CLUB_ID)).length ? "France (synthèse)" : "Sélection de clubs";

  if (!indicators.length) {
    return (
      <div className="v2-kpi-empty">
        {PLATFORM_BY_ID[platform]?.label} : pas d'indicateurs automatiques (données non exposées par l'API).
        Utilisez l'onglet « Campagnes &amp; opérations » pour une saisie manuelle.
      </div>
    );
  }
  if (!scopeCur) {
    return <div className="v2-kpi-empty">Aucune donnée {PLATFORM_BY_ID[platform]?.label} pour {formatMonth(anchorMonth)}.</div>;
  }

  const trendMonths = availableMonths(reportingMap).filter((mk) => mk <= anchorMonth).slice(-8);
  const scopeClubIds = single ? [single.club.id] : targetClubs.map((c) => c.id);
  const sparkColor = single ? single.club.color : "#0071CD";

  return (
    <div className="v2-kpi-dash">
      <div className="v2-kpi-dash__scope">
        {scopeLabel} · {PLATFORM_BY_ID[platform]?.label} · {periodType === "mois" ? formatMonth(anchorMonth) : `${months.length} mois`}
      </div>
      <div className="v2-kpi-tiles">
        {indicators.map((key) => {
          const meta = INDICATORS[key] || { key, label: key, fmt: "int" };
          const value = indicatorValue(scopeCur, key);
          const prevValue = indicatorValue(scopePrev, key);
          const target = single ? resolveTarget(objectives, single.club.id, key, anchorMonth) : null;
          const spark = trendMonths.length >= 2 ? buildSeries(reportingMap, scopeClubIds, platform, key, trendMonths) : null;
          return (
            <KpiTile key={key} meta={meta} value={value} prevValue={prevValue} target={target} spark={spark} sparkColor={sparkColor} />
          );
        })}
      </div>

      {trendMonths.length >= 2 && (
        <TrendSection
          reportingMap={reportingMap} targetClubs={targetClubs} platform={platform}
          indicators={indicators} trendMonths={trendMonths}
        />
      )}

      {targetClubs.length > 1 && withData.length > 0 && (
        <div className="v2-kpi-cmp">
          <div className="v2-block-header"><span className="v2-block-header__label">Comparaison des clubs</span></div>
          <div className="v2-kpi-table-wrap">
            <table className="v2-kpi-table">
              <thead>
                <tr>
                  <th>Club</th>
                  {indicators.slice(0, 6).map((k) => <th key={k}>{INDICATORS[k]?.label || k}</th>)}
                </tr>
              </thead>
              <tbody>
                {withData.map(({ club, cur }) => (
                  <tr key={club.id}>
                    <td><span className="v2-kpi-dot" style={{ background: club.color }} /> {club.name}</td>
                    {indicators.slice(0, 6).map((k) => {
                      const v = indicatorValue(cur, k);
                      const meta = INDICATORS[k] || { fmt: "int" };
                      return <td key={k}>{v == null ? <Indispo /> : fmtValue(v, meta.fmt, meta)}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* Série mensuelle d'un indicateur pour un club (ou agrégat de clubs) sur une liste de mois. */
function buildSeries(reportingMap, clubIds, platform, indicator, months) {
  return months.map((m) => {
    const summaries = clubIds
      .map((cid) => (reportingMap[m] || []).find((r) => String(r.clubId) === String(cid)))
      .map((r) => rowPlatformSummary(r, platform))
      .filter(Boolean);
    if (!summaries.length) return null;
    const combined = clubIds.length === 1 ? summaries[0] : combineSummaries(summaries, "sum");
    return indicatorValue(combined, indicator);
  });
}

/* Mini-courbe SVG pour une tuile KPI. */
function Sparkline({ values, color = "#0071CD", width = 120, height = 26 }) {
  const nums = (values || []).map((v) => (v == null || isNaN(v) ? null : Number(v)));
  const present = nums.filter((v) => v != null);
  if (present.length < 2) return null;
  const min = Math.min(...present);
  const max = Math.max(...present);
  const span = max - min || 1;
  const n = nums.length;
  const pts = [];
  nums.forEach((v, i) => {
    if (v == null) return;
    const x = (n === 1 ? 0 : (i / (n - 1)) * (width - 2)) + 1;
    const y = height - 2 - ((v - min) / span) * (height - 4);
    pts.push([x, y]);
  });
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1];
  return (
    <svg className="v2-kpi-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      {last && <circle cx={last[0]} cy={last[1]} r="2" fill={color} />}
    </svg>
  );
}

/* Graphe multi-lignes (une ligne par club) pour le bloc Tendance. */
function TrendChart({ months, series, fmt }) {
  const [hover, setHover] = useState(null);
  const W = 680, H = 230, padL = 48, padR = 14, padT = 14, padB = 28;
  const all = series.flatMap((s) => s.values).filter((v) => v != null && !isNaN(v));
  if (all.length < 2) return <div className="v2-kpi-empty">Pas assez d'historique pour tracer une courbe.</div>;
  let min = Math.min(...all), max = Math.max(...all);
  if (min === max) { min -= 1; max += 1; }
  const padv = (max - min) * 0.08;
  // Compteurs positifs : on cale le bas de l'axe à 0 (pas de valeurs négatives trompeuses).
  min = Math.min(...all) >= 0 ? Math.max(0, min - padv) : min - padv;
  max += padv;
  const n = months.length;
  const xFor = (i) => padL + (n === 1 ? 0 : (i / (n - 1)) * (W - padL - padR));
  const yFor = (v) => padT + (1 - (v - min) / (max - min)) * (H - padT - padB);
  const yTicks = Array.from({ length: 4 }, (_, i) => min + ((max - min) * i) / 3);
  const pathFor = (vals) => {
    let d = "", started = false;
    vals.forEach((v, i) => {
      if (v == null || isNaN(v)) { started = false; return; }
      d += `${started ? "L" : "M"}${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)} `;
      started = true;
    });
    return d.trim();
  };
  return (
    <svg className="v2-kpi-trend-svg" viewBox={`0 0 ${W} ${H}`} role="img" onMouseLeave={() => setHover(null)}>
      {yTicks.map((t, i) => (
        <g key={i}>
          <line x1={padL} y1={yFor(t)} x2={W - padR} y2={yFor(t)} className="v2-kpi-grid-line" />
          <text x={padL - 6} y={yFor(t) + 3} className="v2-kpi-axis-y">{fmtValue(t, fmt === "pct" ? "pct" : "int")}</text>
        </g>
      ))}
      {months.map((m, i) => (
        (n <= 8 || i % 2 === 0) ? <text key={m} x={xFor(i)} y={H - 8} className="v2-kpi-axis-x">{monthAbbr(m)}</text> : null
      ))}
      {series.map((s) => (
        <path key={s.label} d={pathFor(s.values)} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      ))}
      {series.map((s) => s.values.map((v, i) => (
        v == null || isNaN(v) ? null : <circle key={s.label + i} cx={xFor(i)} cy={yFor(v)} r="2.4" fill={s.color} />
      )))}
      {/* Zones de survol (une par mois) */}
      {months.map((m, i) => {
        const x0 = i === 0 ? padL : (xFor(i - 1) + xFor(i)) / 2;
        const x1 = i === n - 1 ? W - padR : (xFor(i) + xFor(i + 1)) / 2;
        return <rect key={`hz${i}`} x={x0} y={padT} width={Math.max(1, x1 - x0)} height={H - padT - padB} fill="transparent" onMouseEnter={() => setHover(i)} />;
      })}
      {/* Tooltip au survol : guide vertical + points + encart des valeurs */}
      {hover != null && (() => {
        const i = hover;
        const pts = series.filter((s) => s.values[i] != null && !isNaN(s.values[i]));
        if (!pts.length) return null;
        const boxW = 150, lineH = 15, boxH = 12 + (pts.length + 1) * lineH;
        let tx = xFor(i) + 12;
        if (tx + boxW > W) tx = xFor(i) - boxW - 12;
        const ty = padT;
        return (
          <g pointerEvents="none">
            <line x1={xFor(i)} y1={padT} x2={xFor(i)} y2={H - padB} className="v2-kpi-hover-line" />
            {pts.map((s) => (
              <circle key={s.label} cx={xFor(i)} cy={yFor(s.values[i])} r="3.8" fill={s.color} stroke="#fff" strokeWidth="1.5" />
            ))}
            <rect x={tx} y={ty} width={boxW} height={boxH} rx="7" className="v2-kpi-tooltip-box" />
            <text x={tx + 11} y={ty + 17} className="v2-kpi-tooltip-title">{monthAbbr(months[i])}</text>
            {pts.map((s, k) => (
              <g key={s.label}>
                <circle cx={tx + 14} cy={ty + 13 + (k + 1) * lineH} r="3.3" fill={s.color} />
                <text x={tx + 23} y={ty + 17 + (k + 1) * lineH} className="v2-kpi-tooltip-row">{s.label} : {fmtValue(s.values[i], fmt === "pct" ? "pct" : "int")}</text>
              </g>
            ))}
          </g>
        );
      })()}
    </svg>
  );
}

// On trace toutes les métriques de la plateforme, sauf les dérivés déjà exprimés
// en variation (évolution d'abonnés, taux d'évolution).
const TREND_EXCLUDE = ["evolutionAbonnes", "txEvol"];

function TrendSection({ reportingMap, targetClubs, platform, indicators, trendMonths }) {
  const opts = indicators.filter((k) => !TREND_EXCLUDE.includes(k));
  const [metric, setMetric] = useState(opts[0] || indicators[0]);
  const m = opts.includes(metric) ? metric : (opts[0] || indicators[0]);
  const meta = INDICATORS[m] || { fmt: "int", label: m };
  const perClub = targetClubs.length > 0 && targetClubs.length <= 4;
  const series = perClub
    ? targetClubs.map((c) => ({ label: c.name, color: c.color, values: buildSeries(reportingMap, [c.id], platform, m, trendMonths) }))
    : [{ label: "Ensemble", color: "#182231", values: buildSeries(reportingMap, targetClubs.map((c) => c.id), platform, m, trendMonths) }];
  return (
    <div className="v2-kpi-trend">
      <div className="v2-kpi-trend__head">
        <span className="v2-block-header__label">Tendance</span>
        <select className="v2-kpi-select" value={m} onChange={(e) => setMetric(e.target.value)}>
          {opts.map((k) => <option key={k} value={k}>{INDICATORS[k]?.label || k}</option>)}
        </select>
      </div>
      <div className="v2-kpi-trend__legend">
        {series.map((s) => <span key={s.label}><i style={{ background: s.color }} />{s.label}</span>)}
      </div>
      <TrendChart months={trendMonths} series={series} fmt={meta.fmt} />
    </div>
  );
}

function KpiTile({ meta, value, prevValue, target, spark, sparkColor }) {
  const d = deltaPct(value, prevValue);
  const display = value == null ? null : fmtValue(value, meta.fmt, meta);
  return (
    <div className="v2-kpi-tile" title={meta.help || ""}>
      <div className="v2-kpi-tile__label">{meta.label}</div>
      <div className="v2-kpi-tile__value">{display == null ? <Indispo /> : display}</div>
      {spark && <Sparkline values={spark} color={sparkColor} />}
      <div className="v2-kpi-tile__foot">
        {d != null && (
          <span className={`v2-kpi-delta ${d > 0 ? "up" : d < 0 ? "down" : ""}`}>
            {d > 0 ? "▲" : d < 0 ? "▼" : "="} {Math.abs(d)}%
          </span>
        )}
        {target && target.target > 0 && value != null && (
          <span className="v2-kpi-target">
            cible {fmtValue(target.target, meta.fmt, meta)} ·{" "}
            {Math.round((value / target.target) * 100)}%
          </span>
        )}
      </div>
    </div>
  );
}

function Indispo() {
  return <span className="v2-kpi-indispo" title="Donnée non disponible">indisponible</span>;
}

/* ══════════════════════════════════════ publications ════════════════════ */

function PublicationsView({ clubs, targetClubs, platform, anchorMonth, format, campaignId, campagnes }) {
  const keys = useMemo(
    () => targetClubs.map((c) => `ep:reporting-details:${anchorMonth}:${c.id}`),
    [targetClubs, anchorMonth]
  );
  const { details, loading } = useReportingDetails(keys);
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const campaign = (campagnes || []).find((c) => String(c.id) === String(campaignId));
  const inCampaign = (dateStr) => {
    if (!campaign) return true;
    if (!campaign.dateStart && !campaign.dateEnd) return true;
    const d = new Date(dateStr);
    const s = campaign.dateStart ? new Date(campaign.dateStart) : null;
    const e = campaign.dateEnd ? new Date(campaign.dateEnd) : null;
    if (s && d < s) return false;
    if (e && d > new Date(e.getTime() + 86400000)) return false;
    return true;
  };

  const rows = useMemo(() => {
    const out = [];
    targetClubs.forEach((c) => {
      const det = details[`ep:reporting-details:${anchorMonth}:${c.id}`];
      platformPublications(det, platform).forEach((p) => {
        if (format && p.format !== format) return;
        if (!inCampaign(p.date)) return;
        out.push({ ...p, clubName: c.name, clubColor: c.color });
      });
    });
    const dir = sortDir === "asc" ? 1 : -1;
    return out.sort((a, b) => {
      const av = sortKey === "date" ? a.date : Number(a[sortKey] || 0);
      const bv = sortKey === "date" ? b.date : Number(b[sortKey] || 0);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [details, targetClubs, platform, anchorMonth, format, sortKey, sortDir, campaignId]);

  const setSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  };
  const Th = ({ k, children, num }) => (
    <th className={num ? "num" : ""} onClick={() => setSort(k)} style={{ cursor: "pointer" }}>
      {children} {sortKey === k ? (sortDir === "asc" ? "▲" : "▼") : ""}
    </th>
  );

  if (!PLATFORM_INDICATORS[platform]?.length) {
    return <div className="v2-kpi-empty">{PLATFORM_BY_ID[platform]?.label} : pas de publications exploitables par l'API.</div>;
  }
  if (loading) return <div className="v2-kpi-empty">Chargement des publications…</div>;
  if (!rows.length) return <div className="v2-kpi-empty">Aucune publication {PLATFORM_BY_ID[platform]?.label} sur {formatMonth(anchorMonth)}{format ? ` (${format})` : ""}.</div>;

  return (
    <div className="v2-kpi-table-wrap">
      <table className="v2-kpi-table v2-kpi-table--pub">
        <thead>
          <tr>
            <th>Aperçu</th>
            <Th k="date">Date</Th>
            <th>Club</th>
            <th>Format</th>
            <Th k="reach" num>Portée</Th>
            <Th k="views" num>Vues</Th>
            <Th k="interactions" num>Interactions</Th>
            <Th k="engagement" num>Engagement</Th>
            <th>Lien</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={(p.id || "") + i}>
              <td className="v2-kpi-pub-thumb">
                {p.image ? <img src={p.image} alt="" loading="lazy" /> : <div className="v2-kpi-pub-noimg"><PlatformIcon id={platform} size={18} /></div>}
              </td>
              <td>{p.date ? new Date(p.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—"}</td>
              <td><span className="v2-kpi-dot" style={{ background: p.clubColor }} /> {p.clubName}</td>
              <td><span className="v2-kpi-fmt">{p.format}</span></td>
              <td className="num">{p.reach != null ? fmtValue(p.reach, "int") : <Indispo />}</td>
              <td className="num">{p.views != null ? fmtValue(p.views, "int") : <Indispo />}</td>
              <td className="num">{fmtValue(p.interactions || 0, "int")}</td>
              <td className="num">{p.engagement != null ? fmtValue(p.engagement, "pct") : <Indispo />}</td>
              <td>{p.url ? <a href={p.url} target="_blank" rel="noreferrer noopener">↗</a> : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ══════════════════════════════════════ classement ═════════════════════ */

function RankingView({ reportingMap, clubs, platform, anchorMonth, isAdmin, currentUser }) {
  const curYear = Number(anchorMonth.split("-")[0]);
  const [year, setYear] = useState(curYear);
  const availableInd = RANKING_INDICATORS[platform] || [];
  const [indicator, setIndicator] = useState(availableInd[0] || "abonnes");

  // Garde un indicateur valide quand la plateforme change.
  const ind = availableInd.includes(indicator) ? indicator : (availableInd[0] || "abonnes");
  const kind = metricKind(ind);

  const seriesFor = (clubId, yr) =>
    monthsOfYear(yr).map((mk) => {
      const row = (reportingMap[mk] || []).find((r) => String(r.clubId) === String(clubId));
      const s = rowPlatformSummary(row, platform);
      const v = indicatorValue(s, ind);
      return { month: mk, value: v, available: v != null };
    });

  const now = new Date();
  const yearComplete = year < now.getFullYear();

  const perClubGrowth = clubs.map((c) =>
    computeClubGrowth({
      clubId: c.id,
      indicator: ind,
      kind,
      year,
      points: seriesFor(c.id, year),
      prevYearPoints: seriesFor(c.id, year - 1),
      yearComplete,
    })
  );
  const { ranked, unranked, sortKey } = computeGrowthRanking({ perClubGrowth, kind });

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= 2025; y -= 1) yearOptions.push(y);

  const fmtGrowth = (g) => {
    if (kind === "ratio") {
      return <>{g.evolutionPoints != null ? `${g.evolutionPoints > 0 ? "+" : ""}${g.evolutionPoints} pts` : "—"}{g.evolutionPct != null ? ` (${g.evolutionPct > 0 ? "+" : ""}${g.evolutionPct}%)` : ""}</>;
    }
    return g.evolutionPct != null ? `${g.evolutionPct > 0 ? "+" : ""}${g.evolutionPct}%` : "—";
  };
  const valFmt = INDICATORS[ind]?.fmt || "int";

  // Vue club (non-admin) : sa propre progression + position, sans valeurs des autres.
  if (!isAdmin) {
    const myId = (currentUser?.clubs || [])[0];
    const mine = [...ranked, ...unranked].find((g) => String(g.clubId) === String(myId));
    const myRank = ranked.find((g) => String(g.clubId) === String(myId));
    const club = resolveClub(myId, clubs);
    return (
      <div className="v2-kpi-rank">
        <RankControls {...{ year, setYear, yearOptions, ind, setIndicator, availableInd, platform }} />
        {!mine ? (
          <div className="v2-kpi-empty">Pas encore de données de progression pour {club.name} ({year}).</div>
        ) : (
          <div className="v2-kpi-rank-card" style={{ borderColor: club.color }}>
            <div className="v2-kpi-rank-card__club">{club.name}</div>
            <div className="v2-kpi-rank-card__pos">
              {myRank ? `${myRank.rank}ᵉ sur ${ranked.length}` : mine.status === "provisoire" ? "Hors classement (provisoire)" : "Non classé"}
            </div>
            <div className="v2-kpi-rank-card__grid">
              <div><span>Départ</span><b>{mine.baseline != null ? fmtValue(mine.baseline, valFmt) : "—"}</b></div>
              <div><span>Actuel</span><b>{mine.current != null ? fmtValue(mine.current, valFmt) : "—"}</b></div>
              <div><span>Progression</span><b>{fmtGrowth(mine)}</b></div>
            </div>
            {mine.reason && <div className="v2-kpi-rank-card__note">{mine.reason}</div>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="v2-kpi-rank">
      <RankControls {...{ year, setYear, yearOptions, ind, setIndicator, availableInd, platform }} />
      {!availableInd.length ? (
        <div className="v2-kpi-empty">Classement indisponible pour {PLATFORM_BY_ID[platform]?.label}.</div>
      ) : (
        <>
          <div className="v2-kpi-table-wrap">
            <table className="v2-kpi-table">
              <thead>
                <tr>
                  <th>Rang</th><th>Club</th><th className="num">Départ</th>
                  <th className="num">Actuel</th><th className="num">Progression</th><th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((g) => {
                  const club = resolveClub(g.clubId, clubs);
                  return (
                    <tr key={g.clubId}>
                      <td><span className="v2-kpi-rank-badge">{g.rank}</span></td>
                      <td><span className="v2-kpi-dot" style={{ background: club.color }} /> {club.name}</td>
                      <td className="num">{g.baseline != null ? fmtValue(g.baseline, valFmt) : "—"}</td>
                      <td className="num">{g.current != null ? fmtValue(g.current, valFmt) : "—"}</td>
                      <td className="num"><strong>{fmtGrowth(g)}</strong></td>
                      <td>{g.provisional ? <span className="v2-badge v2-badge--yellow">provisoire</span> : <span className="v2-badge v2-badge--green">classé</span>}</td>
                    </tr>
                  );
                })}
                {!ranked.length && (
                  <tr><td colSpan={6} className="v2-kpi-empty-cell">Aucun club classable pour {year} (voir hors classement).</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {unranked.length > 0 && (
            <div className="v2-kpi-unranked">
              <div className="v2-block-header"><span className="v2-block-header__label">Hors classement</span></div>
              {unranked.map((g) => {
                const club = resolveClub(g.clubId, clubs);
                return (
                  <div key={g.clubId} className="v2-kpi-unranked-row">
                    <span><span className="v2-kpi-dot" style={{ background: club.color }} /> {club.name}</span>
                    <span>{g.current != null ? fmtValue(g.current, valFmt) : "—"}</span>
                    <span className="v2-kpi-unranked-reason">{g.reason || g.status}</span>
                  </div>
                );
              })}
            </div>
          )}
          <p className="v2-kpi-rank-help">
            Classement par progression ({sortKey === "evolutionPoints" ? "points de %" : "% d'évolution"}).
            {kind === "flow" ? " Comparaison année N vs N-1 sur la même fenêtre de mois." : kind === "stock" ? " Début → fin de période." : ""}
          </p>
        </>
      )}
    </div>
  );
}

function RankControls({ year, setYear, yearOptions, ind, setIndicator, availableInd }) {
  return (
    <div className="v2-kpi-rank-controls">
      <label>Année
        <select className="v2-kpi-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </label>
      <label>Indicateur
        <select className="v2-kpi-select" value={ind} onChange={(e) => setIndicator(e.target.value)} disabled={!availableInd.length}>
          {availableInd.map((k) => <option key={k} value={k}>{INDICATORS[k]?.label || k}</option>)}
        </select>
      </label>
    </div>
  );
}

/* ══════════════════════════════════════ campagnes / ops ═════════════════ */

const MANUAL_FIELDS = [
  { key: "inscriptions", label: "Inscriptions événement" },
  { key: "contacts", label: "Demandes / contacts" },
  { key: "reservations", label: "Réservations" },
  { key: "budget", label: "Budget dépensé (€)" },
  { key: "resultats", label: "Résultats (payant)" },
];

function CampaignsOpsView({ campagnes, events, clubs, targetClubs, kpiManual, appId, isAdmin }) {
  const [editing, setEditing] = useState(null); // { scope, refId, clubId }
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const entryFor = (scope, refId, clubId) =>
    Object.values(kpiManual || {}).find(
      (e) => e.scope === scope && String(e.refId) === String(refId) && String(e.clubId) === String(clubId || "")
    );

  const openEdit = (scope, refId, clubId) => {
    const ex = entryFor(scope, refId, clubId);
    setForm(ex ? { ...ex.values, note: ex.note || "", _id: ex.id } : { note: "" });
    setEditing({ scope, refId, clubId });
  };
  const save = async () => {
    setBusy(true);
    try {
      const { note, _id, ...values } = form;
      const num = {};
      Object.entries(values).forEach(([k, v]) => { if (v !== "" && v != null) num[k] = Number(v); });
      await upsertManualEntry(kpiManual, {
        id: _id, scope: editing.scope, refId: editing.refId, clubId: editing.clubId || null,
        values: num, note,
      }, appId);
      setEditing(null);
    } finally { setBusy(false); }
  };
  const removeEntry = async (id) => { await deleteManualEntry(kpiManual, id); };

  const costPerResult = (v) => {
    const budget = Number(v?.budget || 0);
    const res = Number(v?.resultats || v?.inscriptions || 0);
    return budget > 0 && res > 0 ? Number((budget / res).toFixed(2)) : null;
  };

  return (
    <div className="v2-kpi-ops">
      <p className="v2-kpi-ops-intro">
        Suivi des campagnes et opérations locales. Les chiffres saisis ici sont marqués
        <span className="v2-badge v2-badge--gray" style={{ margin: "0 4px" }}>manuel</span>
        et complètent les données automatiques.
      </p>

      <div className="v2-block-header"><span className="v2-block-header__label">Campagnes</span></div>
      <div className="v2-kpi-ops-list">
        {(campagnes || []).length === 0 && <div className="v2-kpi-empty">Aucune campagne.</div>}
        {(campagnes || []).map((c) => {
          const platforms = (c.channels || []).map((ch) => CHANNEL_TO_PLATFORM[ch]).filter(Boolean);
          const ex = entryFor("campaign", c.id, "");
          const cpr = ex ? costPerResult(ex.values) : null;
          return (
            <div key={c.id} className="v2-kpi-ops-row">
              <div className="v2-kpi-ops-row__main">
                <div className="v2-kpi-ops-row__title">{c.name || "Campagne"}</div>
                <div className="v2-kpi-ops-row__meta">
                  {c.dateStart ? new Date(c.dateStart).toLocaleDateString("fr-FR") : "—"}
                  {platforms.length ? ` · ${[...new Set(platforms)].map((p) => PLATFORM_BY_ID[p]?.short).join(", ")}` : ""}
                </div>
                {ex && (
                  <div className="v2-kpi-ops-row__vals">
                    {MANUAL_FIELDS.filter((f) => ex.values[f.key] != null).map((f) => (
                      <span key={f.key}>{f.label}: <b>{fmtValue(ex.values[f.key], f.key === "budget" ? "num" : "int")}</b></span>
                    ))}
                    {cpr != null && <span>Coût / résultat: <b>{fmtValue(cpr, "num")} €</b></span>}
                  </div>
                )}
              </div>
              <div className="v2-kpi-ops-row__actions">
                <button className="v2-btn v2-btn--sm" onClick={() => openEdit("campaign", c.id, "")}>{ex ? "Modifier" : "Saisir"}</button>
                {ex && <button className="v2-btn v2-btn--sm v2-btn--ghost" onClick={() => removeEntry(ex.id)}>Suppr.</button>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="v2-block-header" style={{ marginTop: 18 }}><span className="v2-block-header__label">Événements</span></div>
      <div className="v2-kpi-ops-list">
        {(events || []).slice(0, 20).map((ev) => {
          const ex = entryFor("event", ev.id, "");
          return (
            <div key={ev.id} className="v2-kpi-ops-row">
              <div className="v2-kpi-ops-row__main">
                <div className="v2-kpi-ops-row__title">{ev.title || "Événement"}</div>
                <div className="v2-kpi-ops-row__meta">{ev.date ? new Date(ev.date).toLocaleDateString("fr-FR") : "—"}</div>
                {ex && (
                  <div className="v2-kpi-ops-row__vals">
                    {MANUAL_FIELDS.filter((f) => ex.values[f.key] != null).map((f) => (
                      <span key={f.key}>{f.label}: <b>{fmtValue(ex.values[f.key], f.key === "budget" ? "num" : "int")}</b></span>
                    ))}
                  </div>
                )}
              </div>
              <div className="v2-kpi-ops-row__actions">
                <button className="v2-btn v2-btn--sm" onClick={() => openEdit("event", ev.id, "")}>{ex ? "Modifier" : "Saisir"}</button>
                {ex && <button className="v2-btn v2-btn--sm v2-btn--ghost" onClick={() => removeEntry(ex.id)}>Suppr.</button>}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="v2-kpi-modal-overlay" onClick={() => setEditing(null)}>
          <div className="v2-kpi-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Saisie manuelle</h3>
            <div className="v2-kpi-modal-grid">
              {MANUAL_FIELDS.map((f) => (
                <label key={f.key} className="v2-form-group">
                  <span className="v2-form-label">{f.label}</span>
                  <input
                    className="v2-form-input" type="number" min="0"
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                  />
                </label>
              ))}
              <label className="v2-form-group" style={{ gridColumn: "1 / -1" }}>
                <span className="v2-form-label">Note</span>
                <input className="v2-form-input" value={form.note ?? ""} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} />
              </label>
            </div>
            <div className="v2-kpi-modal-actions">
              <button className="v2-btn v2-btn--ghost" onClick={() => setEditing(null)}>Annuler</button>
              <button className="v2-btn v2-btn--primary" onClick={save} disabled={busy}>{busy ? "…" : "Enregistrer"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════ rapports ════════════════════════ */

function downloadAsPdf(html) {
  const frame = document.createElement("iframe");
  Object.assign(frame.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
  document.body.appendChild(frame);
  frame.contentDocument.write(html);
  frame.contentDocument.close();
  setTimeout(() => {
    frame.contentWindow.focus();
    frame.contentWindow.print();
    setTimeout(() => document.body.removeChild(frame), 1000);
  }, 400);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const xmlEsc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * Génère un vrai classeur Excel multi-onglets (SpreadsheetML 2003, ouvert nativement
 * par Excel / LibreOffice) sans aucune dépendance. `sheets` = [{ name, cols?, rows }]
 * où chaque cellule = { v, t?:"String"|"Number", s?:styleID, merge?:n } ou null.
 */
function downloadXls(filename, sheets) {
  const styles = `<Styles>
    <Style ss:ID="Default" ss:Name="Normal"><Font ss:FontName="Calibri" ss:Size="11"/><Alignment ss:Vertical="Center"/></Style>
    <Style ss:ID="title"><Font ss:Bold="1" ss:Size="15" ss:Color="#0071CD"/></Style>
    <Style ss:ID="sub"><Font ss:Italic="1" ss:Size="10" ss:Color="#777777"/></Style>
    <Style ss:ID="hdr"><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#0071CD" ss:Pattern="Solid"/><Alignment ss:Horizontal="Left" ss:Vertical="Center"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#0071CD"/></Borders></Style>
    <Style ss:ID="label"><Font ss:Bold="1" ss:Color="#182231"/></Style>
    <Style ss:ID="int"><NumberFormat ss:Format="#,##0"/></Style>
    <Style ss:ID="num"><NumberFormat ss:Format="#,##0.00"/></Style>
    <Style ss:ID="pct"><NumberFormat ss:Format="0.00&quot;%&quot;"/></Style>
    <Style ss:ID="eur"><NumberFormat ss:Format="#,##0.00\\ &quot;€&quot;"/></Style>
  </Styles>`;
  const cell = (c) => {
    if (c == null) return "<Cell/>";
    const t = c.t || "String";
    const sid = c.s ? ` ss:StyleID="${c.s}"` : "";
    const mc = c.merge ? ` ss:MergeAcross="${c.merge}"` : "";
    const val = t === "Number" ? (c.v == null || c.v === "" ? "" : c.v) : xmlEsc(c.v);
    return `<Cell${sid}${mc}><Data ss:Type="${t}">${val}</Data></Cell>`;
  };
  const sheetXml = sheets.map((sh) => {
    const cols = (sh.cols || []).map((w) => `<Column ss:Width="${w}"/>`).join("");
    const rows = sh.rows.map((r) => `<Row>${r.map(cell).join("")}</Row>`).join("");
    return `<Worksheet ss:Name="${xmlEsc(sh.name)}"><Table>${cols}${rows}</Table></Worksheet>`;
  }).join("");
  const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<?mso-application progid="Excel.Sheet"?>\n<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet" xmlns:html="http://www.w3.org/TR/REC-html40">${styles}${sheetXml}</Workbook>`;
  triggerDownload(new Blob(["﻿" + xml], { type: "application/vnd.ms-excel;charset=utf-8" }), filename);
}

/** Mini-courbe (SVG inline) pour le bilan PDF : série [{month,value}] éventuellement trouée. */
function svgTrend(series, color) {
  const pts = (series || []).map((s, i) => ({ i, v: s.value == null ? null : Number(s.value) }));
  const present = pts.filter((p) => p.v != null);
  if (present.length < 2) return "";
  const W = 540, H = 96, pad = 10, n = pts.length;
  const xs = (i) => pad + (n <= 1 ? 0 : (i / (n - 1)) * (W - 2 * pad));
  const min = Math.min(...present.map((p) => p.v));
  const max = Math.max(...present.map((p) => p.v));
  const ys = (v) => H - pad - ((v - min) / ((max - min) || 1)) * (H - 2 * pad);
  const line = present.map((p, k) => `${k ? "L" : "M"}${xs(p.i).toFixed(1)},${ys(p.v).toFixed(1)}`).join(" ");
  const area = `${line} L${xs(present[present.length - 1].i).toFixed(1)},${H - pad} L${xs(present[0].i).toFixed(1)},${H - pad} Z`;
  const last = present[present.length - 1];
  return `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" style="display:block">
    <path d="${area}" fill="${color}" fill-opacity="0.10"/>
    <path d="${line}" fill="none" stroke="${color}" stroke-width="2.2" stroke-linejoin="round" stroke-linecap="round"/>
    <circle cx="${xs(last.i).toFixed(1)}" cy="${ys(last.v).toFixed(1)}" r="3.4" fill="${color}"/>
  </svg>`;
}

/** Barres horizontales HTML (impression print fiable) : items [{label,value,color}]. */
function htmlBars(items, fmt) {
  const rows = (items || []).filter((it) => it.value != null);
  if (!rows.length) return "";
  const max = Math.max(...rows.map((r) => Number(r.value) || 0), 1);
  return rows.map((r) => {
    const w = Math.max(2, Math.round((Number(r.value) / max) * 100));
    return `<div class="bar"><div class="bar-l">${esc(r.label)}</div><div class="bar-track"><div class="bar-fill" style="width:${w}%;background:${r.color || "#0071CD"}"></div></div><div class="bar-v">${fmtValue(r.value, fmt || "int")}</div></div>`;
  }).join("");
}

const REPORT_PLATFORMS = [
  { id: "instagram", label: "Instagram", color: "#D6249F", keys: ["abonnes", "evolutionAbonnes", "porteeTotale", "engagement", "publications", "reels", "stories"] },
  { id: "facebook", label: "Facebook", color: "#1877F2", keys: ["abonnes", "evolutionAbonnes", "porteeTotale", "interactions", "publications"] },
  { id: "gmb", label: "Google Business", color: "#34A853", keys: ["impressionsTotal", "actionsTotal", "callClicks", "websiteClicks", "reviews"] },
  { id: "web", label: "Site web", color: "#F97316", keys: ["utilisateurs", "sessions", "pagesVues", "tauxEngagement", "dureeMoySession"] },
];
const REPORT_PLATFORM_BY_ID = Object.fromEntries(REPORT_PLATFORMS.map((p) => [p.id, p]));

/**
 * Configuration du bilan par famille de société : plateformes couvertes, indicateur
 * « héros » (courbe de tête) et sections activées. Le bilan s'adapte ainsi au compte
 * sélectionné (club de padel, restaurant Faim de Piste, compte national, synthèse France).
 */
const FAMILY_CONFIG = {
  france:     { label: "Synthèse France (padel)", platforms: ["instagram", "facebook", "gmb", "web"], hero: ["instagram", "abonnes"], perClub: true,  ranking: true,  objectives: true,  campaigns: true },
  padel:      { label: "Club de padel",           platforms: ["instagram", "facebook", "gmb", "web"], hero: ["instagram", "abonnes"], perClub: false, ranking: true,  objectives: true,  campaigns: true },
  restaurant: { label: "Restaurant",              platforms: ["instagram", "facebook", "gmb"],        hero: ["instagram", "abonnes"], perClub: false, ranking: false, objectives: false, campaigns: false },
  national:   { label: "Compte national",         platforms: ["instagram", "facebook", "gmb", "web"], hero: ["instagram", "abonnes"], perClub: false, ranking: false, objectives: false, campaigns: true },
};
const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;");

function ReportsView({ reportingMap, clubs, targetClubs, anchorMonth, objectives, kpiReports, kpiManual, campagnes, events, appId, isAdmin }) {
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);

  const padelClubs = useMemo(() => clubs.filter((c) => String(c.id) !== String(NATIONAL_CLUB_ID)), [clubs]);

  // ── Sociétés proposées au bilan (groupées). Le bilan s'adapte à celle qui est choisie. ──
  const societes = useMemo(() => {
    const list = [];
    if (isAdmin) {
      list.push({ id: "__france", name: "France — synthèse padel", family: "france", group: "Synthèse", color: "#0071CD", clubIds: padelClubs.map((c) => String(c.id)) });
      padelClubs.forEach((c) => list.push({ id: String(c.id), name: c.name, family: "padel", group: "Clubs de padel", color: c.color || "#0071CD", clubIds: [String(c.id)] }));
      REPORTING_ENTITIES.forEach((e) => list.push({ id: String(e.id), name: e.name, family: e.family, group: e.family === "restaurant" ? "Restaurants" : "National", color: e.color, clubIds: [String(e.id)] }));
    } else {
      targetClubs.forEach((c) => list.push({ id: String(c.id), name: c.name, family: "padel", group: "Clubs de padel", color: c.color || "#0071CD", clubIds: [String(c.id)] }));
    }
    return list;
  }, [isAdmin, padelClubs, targetClubs]);
  const grouped = useMemo(() => {
    const g = {};
    societes.forEach((s) => { (g[s.group] = g[s.group] || []).push(s); });
    return g;
  }, [societes]);

  // Société par défaut : la sélection globale si elle désigne une seule entité, sinon la 1ère.
  const defaultSocId = useMemo(() => {
    const ids = targetClubs.map((c) => String(c.id));
    if (ids.length === 1) { const m = societes.find((s) => s.id === ids[0]); if (m) return m.id; }
    return societes[0]?.id || "";
  }, [societes, targetClubs]);
  const [socId, setSocId] = useState(defaultSocId);
  useEffect(() => { if (!societes.some((s) => s.id === socId)) setSocId(defaultSocId); }, [societes, defaultSocId, socId]);

  const soc = societes.find((s) => s.id === socId) || societes[0] || null;
  const fam = FAMILY_CONFIG[soc?.family] || FAMILY_CONFIG.padel;
  const scopeName = soc?.name || "—";
  const scopeClubs = useMemo(() => (soc?.clubIds || []).map((id) => resolveClub(id, clubs)), [soc, clubs]);
  const platforms = REPORT_PLATFORMS.filter((P) => fam.platforms.includes(P.id));

  const detailKeys = useMemo(
    () => scopeClubs.map((c) => `ep:reporting-details:${anchorMonth}:${c.id}`),
    [scopeClubs, anchorMonth]
  );
  const { details } = useReportingDetails(detailKeys);

  const platformAgg = (pid, month) => {
    const sums = scopeClubs
      .map((c) => rowPlatformSummary((reportingMap[month] || []).find((r) => String(r.clubId) === String(c.id)), pid))
      .filter(Boolean);
    if (!sums.length) return null;
    return sums.length === 1 ? sums[0] : combineSummaries(sums, "sum");
  };

  const allPublications = () => {
    const out = [];
    scopeClubs.forEach((c) => {
      const det = details[`ep:reporting-details:${anchorMonth}:${c.id}`];
      fam.platforms.forEach((pid) => {
        if (pid === "gmb" || pid === "web") return;
        platformPublications(det, pid).forEach((p) => out.push({ ...p, club: c.name, platform: pid }));
      });
    });
    return out.sort((a, b) => (b.interactions || 0) - (a.interactions || 0) || (b.reach || 0) - (a.reach || 0));
  };
  const topPosts = () => allPublications().filter((p) => p.format !== "story").slice(0, 5);

  const manualEntries = () => {
    const nameOf = (e) => {
      if (e.scope === "campaign") return (campagnes || []).find((c) => String(c.id) === String(e.refId))?.name || "Campagne";
      if (e.scope === "event") return (events || []).find((v) => String(v.id) === String(e.refId))?.title || "Événement";
      return "Opération locale";
    };
    return Object.values(kpiManual || {})
      .filter((e) => e && e.values && Object.keys(e.values).length)
      .map((e) => ({ name: nameOf(e), values: e.values || {} }));
  };

  const annualRanking = () => {
    const year = Number(anchorMonth.split("-")[0]);
    const yearComplete = year < new Date().getFullYear();
    const seriesFor = (cid) => monthsOfYear(year).map((mk) => {
      const s = rowPlatformSummary((reportingMap[mk] || []).find((r) => String(r.clubId) === String(cid)), "instagram");
      const v = s ? indicatorValue(s, "abonnes") : null;
      return { month: mk, value: v, available: v != null };
    });
    const per = padelClubs.map((c) => computeClubGrowth({ clubId: c.id, indicator: "abonnes", kind: "stock", year, points: seriesFor(c.id), yearComplete }));
    return { year, ...computeGrowthRanking({ perClubGrowth: per, kind: "stock" }) };
  };

  const heroSeries = () => {
    const [hp, hk] = fam.hero;
    const year = Number(anchorMonth.split("-")[0]);
    return monthsOfYear(year).filter((mk) => mk <= anchorMonth).map((mk) => {
      const a = platformAgg(hp, mk);
      return { month: mk, value: a ? indicatorValue(a, hk) : null };
    });
  };

  // Synthèse automatique « En bref » (langage clair), adaptée à la société.
  const bref = () => {
    const out = [];
    const [hp, hk] = fam.hero;
    const c0 = platformAgg(hp, anchorMonth), p0 = platformAgg(hp, prevMonthKey(anchorMonth));
    const span = (d) => d == null ? "" : ` <span class="${d > 0 ? "up" : d < 0 ? "down" : "flat"}">(${d > 0 ? "+" : ""}${d}% vs mois préc.)</span>`;
    if (c0) {
      const v = indicatorValue(c0, hk), d = p0 ? deltaPct(v, indicatorValue(p0, hk)) : null;
      out.push(`<b>${esc(INDICATORS[hk]?.label || hk)} ${REPORT_PLATFORM_BY_ID[hp]?.label}</b> : ${fmtValue(v, INDICATORS[hk]?.fmt)}${span(d)}.`);
    }
    if (fam.platforms.includes("web")) {
      const w = platformAgg("web", anchorMonth), wp = platformAgg("web", prevMonthKey(anchorMonth));
      if (w) { const v = indicatorValue(w, "utilisateurs"); out.push(`<b>Site web</b> : ${fmtValue(v, "int")} utilisateurs${span(wp ? deltaPct(v, indicatorValue(wp, "utilisateurs")) : null)}.`); }
    }
    if (fam.platforms.includes("gmb")) {
      const g = platformAgg("gmb", anchorMonth);
      if (g) {
        const bits = [];
        const imp = indicatorValue(g, "impressionsTotal"), rev = indicatorValue(g, "reviews"), rate = indicatorValue(g, "avgRating");
        if (imp != null) bits.push(`${fmtValue(imp, "int")} impressions`);
        if (rev != null) bits.push(`${fmtValue(rev, "int")} avis`);
        if (rate != null) bits.push(`note ${fmtValue(rate, "num")}/5`);
        if (bits.length) out.push(`<b>Google Business</b> : ${bits.join(" · ")}.`);
      }
    }
    if (fam.ranking) { const { ranked } = annualRanking(); if (ranked && ranked[0]) { const c = resolveClub(ranked[0].clubId, clubs); out.push(`<b>Meilleure progression</b> abonnés IG : ${esc(c.name)} (${ranked[0].evolutionPct > 0 ? "+" : ""}${ranked[0].evolutionPct}%).`); } }
    const tp = topPosts()[0]; if (tp) out.push(`<b>Publication la plus engageante</b> : ${fmtValue(tp.reach || 0, "int")} de portée${tp.club ? ` (${esc(tp.club)})` : ""}.`);
    return out;
  };

  const buildHtml = () => {
    const title = `Bilan communication — ${scopeName}`;
    const prev = prevMonthKey(anchorMonth);
    const [hp, hk] = fam.hero;
    const heroColor = REPORT_PLATFORM_BY_ID[hp]?.color || "#0071CD";

    let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
      *{box-sizing:border-box;margin:0;padding:0} body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#182231;font-size:11px}
      .page{padding:14mm 13mm;max-width:210mm;margin:0 auto}
      .header{background:#0071CD;color:#fff;padding:18px 24px;border-radius:10px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:flex-end}
      .header h1{font-size:20px} .header .sub{font-size:10px;opacity:.8;margin-top:4px} .header .per{font-size:13px;font-weight:700;color:#FFB600;text-align:right}
      .header .tag{display:inline-block;margin-top:6px;font-size:8.5px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;background:rgba(255,255,255,.18);padding:2px 9px;border-radius:20px}
      h2{font-size:12px;color:#0071CD;margin:22px 0 10px;padding-bottom:5px;border-bottom:2px solid #FFB600;text-transform:uppercase;letter-spacing:.03em}
      .brief{background:#F4F7FC;border:1px solid #DCE6F5;border-left:4px solid #0071CD;border-radius:8px;padding:12px 14px;margin-bottom:16px}
      .brief-t{font-size:9.5px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:#0071CD;margin-bottom:6px}
      .brief ul{margin:0;padding-left:16px} .brief li{font-size:10.5px;line-height:1.6;margin:2px 0}
      .hero{background:#fff;border:1px solid #eee;border-radius:10px;padding:12px 14px;margin-bottom:4px}
      .hero-h{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:4px}
      .hero-l{font-size:9px;text-transform:uppercase;letter-spacing:.05em;color:#888} .hero-v{font-size:22px;font-weight:800;color:#182231}
      .hero-d{font-size:12px;font-weight:700;text-align:right} .hero-d span{display:block;font-size:8px;font-weight:600;color:#aaa}
      .hero-ax{display:flex;justify-content:space-between;font-size:8px;color:#aaa;margin-top:2px}
      .bars{margin:4px 0 12px} .bar{display:flex;align-items:center;gap:8px;margin:3px 0} .bar-l{width:130px;font-size:9.5px;color:#444;text-align:right;flex-shrink:0;overflow:hidden;white-space:nowrap;text-overflow:ellipsis} .bar-track{flex:1;background:#f0f0f0;border-radius:4px;height:13px;overflow:hidden} .bar-fill{height:100%;border-radius:4px} .bar-v{width:72px;font-size:9.5px;font-weight:700;flex-shrink:0;text-align:right}
      .plat{margin-bottom:12px} .pname{font-size:12px;font-weight:800;margin-bottom:6px;display:flex;align-items:center;gap:7px} .pdot{width:11px;height:11px;border-radius:3px;display:inline-block}
      .cards{display:flex;flex-wrap:wrap;gap:8px} .card{flex:1;min-width:104px;background:#F7F6F3;border:1px solid #eee;border-radius:8px;padding:9px 11px}
      .card .cv{font-size:16px;font-weight:800} .card .cl{font-size:8px;color:#777;text-transform:uppercase;margin-top:2px} .card .cd{font-size:9px;font-weight:700;margin-top:3px}
      .up{color:#1a7a38}.down{color:#bf3327}.flat{color:#999}
      table{width:100%;border-collapse:collapse;margin:6px 0 10px;font-size:10px} th{background:#0071CD;color:#fff;padding:6px 8px;text-align:left;font-size:9px} td{padding:5px 8px;border-bottom:1px solid #eee}
      .post{display:flex;gap:9px;align-items:center;padding:7px 0;border-bottom:1px solid #eee} .post img{width:38px;height:38px;object-fit:cover;border-radius:5px} .post .pm{flex:1;min-width:0} .post .pt{font-size:10px;color:#444;overflow:hidden;white-space:nowrap;text-overflow:ellipsis} .post .psub{font-size:8.5px;color:#999;margin-top:2px} .post .pr{text-align:right;font-size:9px;white-space:nowrap}
      .cmt{white-space:pre-wrap;background:#FEF8E4;border-left:3px solid #FFB600;border-radius:6px;padding:11px;font-size:10.5px}
      .rk{display:inline-block;width:18px;height:18px;line-height:18px;text-align:center;border-radius:50%;background:#FFB600;color:#182231;font-weight:800;font-size:9px}
      .footer{margin-top:26px;border-top:1px solid #eee;padding-top:8px;color:#999;font-size:8px;text-align:center}
      @media print{.page{padding:9mm}}</style></head><body><div class="page">
      <div class="header"><div><h1>Bilan communication</h1><div class="sub">Esprit Padel · généré le ${new Date().toLocaleDateString("fr-FR")}</div><span class="tag">${esc(fam.label)}</span></div><div class="per">${esc(scopeName)}<br>${formatMonth(anchorMonth)}</div></div>`;

    // En bref — synthèse automatique
    const bullets = bref();
    if (bullets.length) html += `<div class="brief"><div class="brief-t">En bref</div><ul>${bullets.map((b) => `<li>${b}</li>`).join("")}</ul></div>`;

    // Courbe de tête (indicateur héros sur l'année)
    const hs = heroSeries();
    const svg = svgTrend(hs, heroColor);
    const cur = platformAgg(hp, anchorMonth), prevHero = platformAgg(hp, prev);
    if (svg && cur) {
      const v = indicatorValue(cur, hk);
      const d = prevHero ? deltaPct(v, indicatorValue(prevHero, hk)) : null;
      html += `<div class="hero"><div class="hero-h"><div><div class="hero-l">${esc(INDICATORS[hk]?.label || hk)} · ${REPORT_PLATFORM_BY_ID[hp]?.label}</div><div class="hero-v">${fmtValue(v, INDICATORS[hk]?.fmt)}</div></div>${d != null ? `<div class="hero-d ${d > 0 ? "up" : d < 0 ? "down" : "flat"}">${d > 0 ? "▲" : d < 0 ? "▼" : "="} ${Math.abs(d)}%<span>vs mois préc.</span></div>` : ""}</div>${svg}<div class="hero-ax"><span>${formatMonth(hs[0]?.month)}</span><span>${formatMonth(hs[hs.length - 1]?.month)}</span></div></div>`;
    }

    // 1. Chiffres clés par plateforme (familles concernées), avec évolution vs mois précédent
    html += `<h2>Chiffres clés</h2>`;
    let anyPlatform = false;
    platforms.forEach((P) => {
      const agg = platformAgg(P.id, anchorMonth);
      if (!agg) return;
      const prevAgg = platformAgg(P.id, prev);
      let cards = "";
      P.keys.forEach((k) => {
        const v = indicatorValue(agg, k);
        if (v == null) return;
        const meta = INDICATORS[k] || { fmt: "int" };
        const d = prevAgg ? deltaPct(v, indicatorValue(prevAgg, k)) : null;
        const darrow = d == null ? "" : `<div class="cd ${d > 0 ? "up" : d < 0 ? "down" : "flat"}">${d > 0 ? "▲" : d < 0 ? "▼" : "="} ${Math.abs(d)}%</div>`;
        cards += `<div class="card"><div class="cv">${fmtValue(v, meta.fmt, meta)}</div><div class="cl">${esc(meta.label || k)}</div>${darrow}</div>`;
      });
      if (!cards) return;
      anyPlatform = true;
      html += `<div class="plat"><div class="pname"><span class="pdot" style="background:${P.color}"></span>${P.label}</div><div class="cards">${cards}</div></div>`;
    });
    if (!anyPlatform) html += `<p>Aucune donnée pour cette période.</p>`;

    // Répartition de la portée (réseaux sociaux)
    const mix = ["instagram", "facebook"].filter((pid) => fam.platforms.includes(pid)).map((pid) => {
      const a = platformAgg(pid, anchorMonth);
      return a ? { label: REPORT_PLATFORM_BY_ID[pid].label, value: indicatorValue(a, "porteeTotale"), color: REPORT_PLATFORM_BY_ID[pid].color } : null;
    }).filter((x) => x && x.value != null);
    if (mix.length >= 2) html += `<h2>Répartition de la portée — réseaux sociaux</h2><div class="bars">${htmlBars(mix, "int")}</div>`;

    // 1b. Par club (synthèse France)
    if (fam.perClub) {
      html += `<h2>Par club — abonnés Instagram</h2><div class="bars">${htmlBars(padelClubs.map((c) => {
        const s = rowPlatformSummary((reportingMap[anchorMonth] || []).find((r) => String(r.clubId) === String(c.id)), "instagram");
        return { label: c.name, value: s ? indicatorValue(s, "abonnes") : null, color: resolveClub(c.id, clubs).color };
      }), "int")}</div>`;
      const cols = [["instagram", "engagement", "Engag. IG"], ["instagram", "porteeTotale", "Portée IG"], ["facebook", "abonnes", "Abonnés FB"], ["gmb", "impressionsTotal", "Impr. GMB"], ["web", "utilisateurs", "Util. web"]];
      html += `<table><tr><th>Club</th>${cols.map((c) => `<th>${c[2]}</th>`).join("")}</tr>`;
      padelClubs.forEach((c) => {
        html += `<tr><td>${esc(c.name)}</td>${cols.map(([pid, k]) => {
          const s = rowPlatformSummary((reportingMap[anchorMonth] || []).find((r) => String(r.clubId) === String(c.id)), pid);
          const v = s ? indicatorValue(s, k) : null;
          return `<td>${v != null ? fmtValue(v, INDICATORS[k]?.fmt || "int", INDICATORS[k]) : "—"}</td>`;
        }).join("")}</tr>`;
      });
      html += `</table>`;
    }

    // 2. Objectifs
    if (fam.objectives) {
      const relObj = (objectives || []).filter((o) => (fam.perClub ? true : String(o.club) === String(soc.id)) && String(o.metricPeriodTo || "").slice(0, 7) === anchorMonth);
      if (relObj.length) {
        html += `<h2>Objectifs du mois</h2><table><tr><th>Objectif</th><th>Cible</th><th>Actuel</th><th>Avancement</th></tr>`;
        relObj.forEach((o) => { html += `<tr><td>${esc(o.title)}</td><td>${o.target}</td><td>${o.current ?? "—"}</td><td>${o.progress != null ? Math.round(o.progress) + "%" : "—"}</td></tr>`; });
        html += `</table>`;
      }
    }

    // 3. Top publications
    const tops = topPosts();
    if (tops.length) {
      html += `<h2>Top publications du mois</h2>`;
      tops.forEach((p) => {
        html += `<div class="post">${p.image ? `<img src="${p.image}">` : ""}<div class="pm"><div class="pt">${esc((p.text || "").slice(0, 80)) || "(sans texte)"}</div><div class="psub">${esc(p.club)} · ${PLATFORM_BY_ID[p.platform]?.label} · ${p.format} · ${p.date ? new Date(p.date).toLocaleDateString("fr-FR") : ""}</div></div><div class="pr"><b>${fmtValue(p.reach || 0, "int")}</b> portée<br><b>${fmtValue(p.interactions || 0, "int")}</b> inter.${p.engagement != null ? ` · ${p.engagement}%` : ""}</div></div>`;
      });
    }

    // 4. Campagnes & opérations (saisie manuelle)
    if (fam.campaigns) {
      const man = manualEntries();
      if (man.length) {
        html += `<h2>Campagnes & opérations</h2><table><tr><th>Opération</th><th>Inscriptions</th><th>Contacts</th><th>Réservations</th><th>Budget</th><th>Coût / résultat</th></tr>`;
        man.forEach((e) => {
          const v = e.values;
          const res = Number(v.resultats || v.inscriptions || 0);
          const cpr = Number(v.budget || 0) > 0 && res > 0 ? `${(Number(v.budget) / res).toFixed(2)} €` : "—";
          html += `<tr><td>${esc(e.name)}</td><td>${v.inscriptions ?? "—"}</td><td>${v.contacts ?? "—"}</td><td>${v.reservations ?? "—"}</td><td>${v.budget != null ? v.budget + " €" : "—"}</td><td>${cpr}</td></tr>`;
        });
        html += `</table>`;
      }
    }

    // 5. Progression annuelle & classement (abonnés IG)
    if (fam.ranking) {
      const { year, ranked, unranked } = annualRanking();
      if (ranked.length || unranked.length) {
        html += `<h2>Progression annuelle ${year} — abonnés Instagram</h2>`;
        if (!fam.perClub) {
          const mine = [...ranked, ...unranked].find((g) => String(g.clubId) === String(soc.id));
          const rk = ranked.find((g) => String(g.clubId) === String(soc.id));
          if (mine) {
            html += `<p>${esc(scopeName)} : ${mine.baseline != null ? fmtValue(mine.baseline, "int") : "—"} → ${mine.current != null ? fmtValue(mine.current, "int") : "—"} abonnés${mine.evolutionPct != null ? ` (${mine.evolutionPct > 0 ? "+" : ""}${mine.evolutionPct}%)` : ""}${rk ? ` — <b>${rk.rank}ᵉ sur ${ranked.length}</b> en progression` : ` — ${mine.reason || "non classé"}`}.</p>`;
          }
        } else {
          html += `<table><tr><th>Rang</th><th>Club</th><th>Début ${year}</th><th>Actuel</th><th>Progression</th></tr>`;
          ranked.forEach((g) => { const c = resolveClub(g.clubId, clubs); html += `<tr><td><span class="rk">${g.rank}</span></td><td>${esc(c.name)}</td><td>${fmtValue(g.baseline || 0, "int")}</td><td>${fmtValue(g.current || 0, "int")}</td><td><b>${g.evolutionPct > 0 ? "+" : ""}${g.evolutionPct}%</b></td></tr>`; });
          html += `</table>`;
        }
      }
    }

    // 6. Commentaires
    if (comments.trim()) html += `<h2>Commentaires, enseignements & prochaines actions</h2><div class="cmt">${esc(comments)}</div>`;
    html += `<div class="footer">Esprit Padel Communication — ${esc(title)} — ${formatMonth(anchorMonth)}</div></div></body></html>`;

    const snapshot = {};
    platforms.forEach((P) => { const a = platformAgg(P.id, anchorMonth); if (a) snapshot[P.id] = a; });
    return { html, title, snapshot };
  };

  const exportPdf = () => { downloadAsPdf(buildHtml().html); };

  const fileBase = `Bilan_${(scopeName || "bilan").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^\w -]+/g, "").trim().replace(/\s+/g, "_")}_${anchorMonth}`;

  // Vrai classeur Excel multi-onglets (SpreadsheetML), adapté à la société.
  const exportXls = () => {
    const prev = prevMonthKey(anchorMonth);
    const num = (v, s) => (v == null ? null : { v: Number(v), t: "Number", s: s || "int" });
    const str = (v, s) => ({ v: v == null ? "" : String(v), t: "String", s });
    const sheets = [];

    // Onglet Synthèse
    const syn = [];
    syn.push([{ v: `Bilan communication — ${scopeName}`, t: "String", s: "title", merge: 3 }]);
    syn.push([{ v: `${fam.label} · ${formatMonth(anchorMonth)} · généré le ${new Date().toLocaleDateString("fr-FR")}`, t: "String", s: "sub", merge: 3 }]);
    syn.push([]);
    syn.push([str("Plateforme", "hdr"), str("Indicateur", "hdr"), str("Valeur", "hdr"), str("Évol. vs mois préc.", "hdr")]);
    platforms.forEach((P) => {
      const agg = platformAgg(P.id, anchorMonth); if (!agg) return;
      const prevAgg = platformAgg(P.id, prev);
      (PLATFORM_INDICATORS[P.id] || []).forEach((k) => {
        const v = indicatorValue(agg, k); if (v == null) return;
        const meta = INDICATORS[k] || {};
        const st = meta.fmt === "pct" ? "pct" : meta.fmt === "num" ? "num" : "int";
        const d = prevAgg ? deltaPct(v, indicatorValue(prevAgg, k)) : null;
        syn.push([str(P.label), str(meta.label || k), num(v, st), d == null ? str("—") : { v: d, t: "Number", s: "pct" }]);
      });
    });
    sheets.push({ name: "Synthèse", cols: [150, 220, 120, 150], rows: syn });

    // Onglet Par club (synthèse France)
    if (fam.perClub) {
      const cols = [["instagram", "abonnes", "Abonnés IG", "int"], ["instagram", "engagement", "Engag. IG", "pct"], ["instagram", "porteeTotale", "Portée IG", "int"], ["facebook", "abonnes", "Abonnés FB", "int"], ["gmb", "impressionsTotal", "Impr. GMB", "int"], ["web", "utilisateurs", "Util. web", "int"]];
      const rows = [[str("Club", "hdr"), ...cols.map((c) => str(c[2], "hdr"))]];
      padelClubs.forEach((c) => {
        rows.push([str(c.name, "label"), ...cols.map(([pid, k, , st]) => {
          const s = rowPlatformSummary((reportingMap[anchorMonth] || []).find((r) => String(r.clubId) === String(c.id)), pid);
          const v = s ? indicatorValue(s, k) : null;
          return v == null ? str("—") : num(v, st);
        })]);
      });
      sheets.push({ name: "Par club", cols: [150, 110, 110, 110, 110, 110], rows });
    }

    // Onglet Publications
    const pubs = allPublications().slice(0, 200);
    if (pubs.length) {
      const rows = [[str("Date", "hdr"), str("Club", "hdr"), str("Plateforme", "hdr"), str("Format", "hdr"), str("Portée", "hdr"), str("Vues", "hdr"), str("Interactions", "hdr"), str("Engagement", "hdr"), str("Lien", "hdr")]];
      pubs.forEach((p) => rows.push([
        str(p.date ? new Date(p.date).toLocaleDateString("fr-FR") : "—"),
        str(p.club || "—"),
        str(REPORT_PLATFORM_BY_ID[p.platform]?.label || p.platform),
        str(p.format || "—"),
        num(p.reach, "int"), num(p.views != null ? p.views : p.vues, "int"), num(p.interactions, "int"),
        p.engagement == null ? str("—") : { v: Number(p.engagement), t: "Number", s: "pct" },
        str(p.url || p.link || p.permalink || ""),
      ]));
      sheets.push({ name: "Publications", cols: [90, 120, 110, 70, 90, 90, 100, 100, 260], rows });
    }

    // Onglet Campagnes
    if (fam.campaigns) {
      const man = manualEntries();
      if (man.length) {
        const rows = [[str("Opération", "hdr"), str("Inscriptions", "hdr"), str("Contacts", "hdr"), str("Réservations", "hdr"), str("Budget", "hdr"), str("Coût / résultat", "hdr")]];
        man.forEach((e) => {
          const v = e.values; const res = Number(v.resultats || v.inscriptions || 0);
          const cpr = Number(v.budget || 0) > 0 && res > 0 ? Number((Number(v.budget) / res).toFixed(2)) : null;
          rows.push([str(e.name, "label"), num(v.inscriptions), num(v.contacts), num(v.reservations), v.budget == null ? str("—") : num(v.budget, "eur"), cpr == null ? str("—") : num(cpr, "eur")]);
        });
        sheets.push({ name: "Campagnes", cols: [220, 100, 100, 110, 100, 120], rows });
      }
    }

    downloadXls(`${fileBase}.xls`, sheets);
  };

  const persist = async () => {
    setBusy(true);
    try {
      const { title, snapshot } = buildHtml();
      await saveReport(kpiReports, {
        type: soc?.family || "club",
        clubId: soc && soc.family !== "france" ? soc.id : null,
        period: anchorMonth, platform: "all", title, comments, snapshot: snapshot || null,
      }, appId);
      setComments("");
    } finally { setBusy(false); }
  };

  const myClubIds = targetClubs.map((c) => String(c.id));
  const visibleReports = isAdmin
    ? kpiReports
    : (kpiReports || []).filter((r) => r.clubId != null && myClubIds.includes(String(r.clubId)));

  return (
    <div className="v2-kpi-reports">
      <div className="v2-kpi-report-builder">
        <div className="v2-kpi-report-builder__row">
          <label>Société
            <select className="v2-kpi-select" value={socId} onChange={(e) => setSocId(e.target.value)}>
              {Object.entries(grouped).map(([grp, items]) => (
                <optgroup key={grp} label={grp}>
                  {items.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          <div className="v2-kpi-report-period">{fam.label} · {formatMonth(anchorMonth)}</div>
        </div>
        <label className="v2-form-group">
          <span className="v2-form-label">Commentaires, enseignements et prochaines actions</span>
          <textarea className="v2-form-input" rows={4} value={comments} onChange={(e) => setComments(e.target.value)} />
        </label>
        <div className="v2-kpi-report-actions">
          <button className="v2-btn v2-btn--primary" onClick={exportPdf}>⬇ Bilan PDF</button>
          <button className="v2-btn" onClick={exportXls}>⬇ Classeur Excel</button>
          <button className="v2-btn v2-btn--ghost" onClick={persist} disabled={busy}>{busy ? "…" : "Enregistrer dans l'historique"}</button>
        </div>
      </div>

      <div className="v2-block-header" style={{ marginTop: 18 }}><span className="v2-block-header__label">Historique des bilans</span></div>
      <div className="v2-kpi-report-history">
        {(!visibleReports || !visibleReports.length) && <div className="v2-kpi-empty">Aucun bilan enregistré.</div>}
        {(visibleReports || []).map((r) => (
          <div key={r.id} className="v2-kpi-report-item">
            <div>
              <div className="v2-kpi-report-item__title">{r.title}</div>
              <div className="v2-kpi-report-item__meta">
                {formatMonth(r.period)} · {r.platform === "all" ? "Toutes plateformes" : (PLATFORM_BY_ID[r.platform]?.label || r.platform)} · {r.createdAt ? new Date(r.createdAt).toLocaleDateString("fr-FR") : ""}
              </div>
            </div>
            {isAdmin && <button className="v2-btn v2-btn--sm v2-btn--ghost" onClick={() => deleteReport(kpiReports, r.id)}>Supprimer</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
